import {
  ALGOLIA_INDEX_NAME,
  DEFAULT_SORT_ORDER,
  SORT_ORDER_TO_ALGOLIA_INDEX,
  ALGOLIA_INDEX_TO_SORT_ORDER,
} from 'commons/constants';

const NON_LIST_FILTER_IDS = new Set([
  'search',
  'quickship',
  'widthRange',
  'color',
]);

const PRESERVED_URL_PARAMS = new Set(['showMultipleStores']);

const parseArrayParam = value => {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) {
    return value.flatMap(item =>
      String(item)
        .split(',')
        .filter(Boolean)
    );
  }
  return String(value)
    .split(',')
    .filter(Boolean);
};

const getFilterParamKeys = (filters = []) =>
  new Set([
    'sortBy',
    'pages',
    'quickship',
    'widthRange',
    'search',
    'color',
    ...(filters || []).map(({ id }) => id),
  ]);

export const parseCategoryPageUrlState = (searchParams, filters = []) => {
  const params = Object.fromEntries(searchParams);
  const filterIds = new Set((filters || []).map(({ id }) => id));

  const sortBy = params.sortBy ?? DEFAULT_SORT_ORDER;
  const currentProductIndex =
    SORT_ORDER_TO_ALGOLIA_INDEX[sortBy] ?? ALGOLIA_INDEX_NAME.PRODUCTS;

  const quickshipValues = parseArrayParam(params.quickship);
  const isReadyToShipActive =
    quickshipValues.includes('true') || quickshipValues.includes(true);

  const widthRangeValue = parseArrayParam(params.widthRange)[0] ?? '';
  const [minWidth = '', maxWidth = ''] = widthRangeValue.split('-');

  const searchQuery = parseArrayParam(params.search)[0] ?? '';

  const selectedColors = parseArrayParam(params.color).reduce(
    (acc, colorId) => ({
      ...acc,
      [colorId]: true,
    }),
    {}
  );

  const selectedFacets = {};
  Object.entries(params).forEach(([key, value]) => {
    if (
      !filterIds.has(key) ||
      NON_LIST_FILTER_IDS.has(key) ||
      PRESERVED_URL_PARAMS.has(key)
    ) {
      return;
    }

    const filter = filters.find(({ id }) => id === key);
    if (!filter) return;

    const labels = new Set();
    parseArrayParam(value).forEach(optionId => {
      const option = filter.options?.find(({ id }) => id === optionId);
      if (option?.label) {
        labels.add(option.label);
      }
    });

    if (labels.size > 0) {
      selectedFacets[key] = labels;
    }
  });

  return {
    sortBy,
    currentProductIndex,
    isReadyToShipActive,
    minWidth,
    maxWidth,
    searchQuery,
    selectedColors,
    selectedFacets,
    colorIds: Object.keys(selectedColors),
  };
};

export const buildCategoryPageUrlParams = ({
  sortBy,
  isReadyToShipActive,
  minWidth,
  maxWidth,
  searchQuery,
  selectedColors,
  selectedFacets,
  filters = [],
  pages,
  existingParams = {},
}) => {
  const filterParamKeys = getFilterParamKeys(filters);
  const params = Object.fromEntries(
    Object.entries(existingParams).filter(
      ([key]) => !filterParamKeys.has(key) || PRESERVED_URL_PARAMS.has(key)
    )
  );

  if (sortBy && sortBy !== DEFAULT_SORT_ORDER) {
    params.sortBy = sortBy;
  }

  if (pages > 0) {
    params.pages = pages;
  }

  if (isReadyToShipActive) {
    params.quickship = 'true';
  }

  if (minWidth || maxWidth) {
    params.widthRange = `${minWidth || ''}-${maxWidth || ''}`;
  }

  if (searchQuery) {
    params.search = searchQuery;
  }

  const activeColorIds = Object.entries(selectedColors || {})
    .filter(([, isSelected]) => isSelected)
    .map(([colorId]) => colorId);

  if (activeColorIds.length > 0) {
    params.color = activeColorIds.join(',');
  }

  Object.entries(selectedFacets || {}).forEach(([attribute, values]) => {
    const filter = filters.find(({ id }) => id === attribute);
    if (!filter || !values?.size) return;

    const optionIds = [...values]
      .map(label => filter.options?.find(option => option.label === label)?.id)
      .filter(Boolean);

    if (optionIds.length > 0) {
      params[attribute] = optionIds.join(',');
    }
  });

  return params;
};

export const getSortByFromProductIndex = productIndex =>
  ALGOLIA_INDEX_TO_SORT_ORDER[productIndex] ?? DEFAULT_SORT_ORDER;

export const serializeFilterState = ({
  sortBy,
  isReadyToShipActive,
  minWidth,
  maxWidth,
  searchQuery,
  selectedColors,
  selectedFacets,
}) =>
  JSON.stringify({
    sortBy,
    isReadyToShipActive,
    minWidth,
    maxWidth,
    searchQuery,
    selectedColors,
    selectedFacets: Object.fromEntries(
      Object.entries(selectedFacets || {}).map(([key, values]) => [
        key,
        [...values].sort(),
      ])
    ),
  });
